import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import * as lodash from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { RoutePaths } from '../app.routes';
import { AccessIdService } from '../services/access-id.service';

export interface RouterState {
  subFolder?: string;
  path?: string;
  query?: {
    accessId?: string;
    accessToken?: string;
  };
  initialized: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RouterService {
  public routerState$ = new BehaviorSubject<RouterState>({
    initialized: false,
  });
  public staticRoutes = ['agb', 'datenschutz', '/agb', '/datenschutz'];

  constructor(
    public router: Router,
    private location: Location,
    private accessIdService: AccessIdService,
  ) {
    this.initialize();

    this.routerState$.subscribe((state) => {
      console.log('RouterState Changed', state);
  
      if (!state.initialized && (state.subFolder === undefined || state.subFolder === 'undefined') &&
        !this.staticRoutes.includes(state.path ?? '')
      ) {
        this.setState({
          subFolder: 'europace',
          path: 'unterlagenauswahl',
          query: {},
          initialized: true,
        });
        this.router.navigate(['europace/unterlagenauswahl'], {});
      } else if (this.staticRoutes.includes(state.path ?? '')) {
        this.router.navigate([state.path ?? ''], {});
        return;
      }

      if (state.initialized && this.getAccessId() && this.accessIdService.currentAccessId() !== state.query?.accessId) {
        this.accessIdService.setAccessId(state.query?.accessId)
      }
      if (state.initialized && state.query?.accessId === undefined) {
        this.accessIdService.clearAccessId()
      }
    });


    this.accessIdService.accessIdState$.subscribe((state) => {
      if (state.initialized && state.accessId !== this.getAccessId()) {
        this.routeToInCurrentSubfolder('unterlagenauswahl', { accessId: state.accessId });
      }
    });
  }

  private handleRouting(currentState?: RouterState) {
    const splittedUrl = this.location.path().split('?')[0].split('/');
    const urlParams = new URLSearchParams(this.location.path().split('?')[1]);
    if(splittedUrl.length === 1 && splittedUrl[0] === '') {
      this.setState({
        subFolder: 'europace',
        path: 'unterlagenauswahl',
        query: {},
        initialized: true,
      });
      this.routeToInCurrentSubfolder('unterlagenauswahl');
    }

    if (splittedUrl.length === 2 && this.staticRoutes.includes(splittedUrl[1])) {
      this.setState({
        subFolder: undefined,
        path: splittedUrl[1],
        query: {},
        initialized: true,
      });
      this.router.navigate([splittedUrl[1]], {});
      return;
    }

    if (splittedUrl.length > 1) {
      const subFolder = splittedUrl[1];
      const accessToken = urlParams.get('accessToken') ?? undefined;
      if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
      }
      const accessId = urlParams.get('accessId') ?? undefined;
      if (splittedUrl.length > 2) {
        const path = splittedUrl[2];
        const stateToSet = {
          subFolder,
          path: path,
          query: { accessId, accessToken },
          initialized: true,
        };
        if (!lodash.isEqual(stateToSet, currentState)) {
          this.setState(stateToSet);
        }
      } else {
        this.setState({
          subFolder,
          query: { accessId, accessToken },
          path: 'unterlagenauswahl',
          initialized: true,
        });
        this.routeToInCurrentSubfolder('unterlagenauswahl');
      }
    }
  }

  private initialize() {
    this.handleRouting();
  }

  public getValue() {
    return this.routerState$.getValue();
  }

  public getState() {
    return this.routerState$;
  }

  public getRouteUrl(
    path: RoutePaths,
    params?: Params,
    fragment?: string,
  ): string {
    const urlTree = this.router.createUrlTree(
      [`${this.routerState$.getValue().subFolder}/${path}`],
      {
        queryParams: params || this.getValue().query,
        fragment: fragment || undefined,
      },
    );
    return this.location.prepareExternalUrl(this.router.serializeUrl(urlTree));
  }

  public async clearState() {
    const subFolder = this.routerState$.getValue().subFolder;
    const path = 'unterlagenauswahl';
    const res = await this.router.navigate([subFolder, path], { queryParams: {} });
    this.routerState$.next({
      initialized: true,
      subFolder: subFolder,
      path: path,
      query: {
        accessId: undefined,
      },
    });
    return res;
  }

  public routeToInCurrentSubfolder(
    path: RoutePaths,
    params: Params | null = null,
    fragment: string | null = null,
  ) {
    const queryParams = params ? params : this.getValue().query;
    const accessId = this.accessIdService.accessIdState$.getValue().initialized ? this.accessIdService.currentAccessId() : params?.accessId;
    this.router.navigate(
      [`${this.routerState$.getValue().subFolder}/` + path],
      {
        queryParams: {
          ...queryParams,
          accessId: accessId,
        }, fragment: fragment || undefined,
      },
    );
  }

  public isCurrentRoute(path: RoutePaths) {
    return this.routerState$.getValue().path === path;
  }

  public unknownPartner() {
    this.router.navigate(['unbekannt'], { queryParams: {} });
  }

  public getAccessId() {
    return this.routerState$.getValue().query?.accessId;
  }

  public getAccessToken() {
    return this.routerState$.getValue().query?.accessToken;
  }

  private setState(routerState?: RouterState) {
    const currentRouterState = this.routerState$.getValue();
    const accessId = this.accessIdService.accessIdState$.getValue().initialized ? this.accessIdService.currentAccessId() : routerState?.query?.accessId ?? currentRouterState.query?.accessId;
    this.routerState$.next({ ...currentRouterState, ...routerState, query: { ...currentRouterState.query, ...routerState?.query, accessId: accessId } });
  }
}
